import { useState, useMemo, useReducer, useCallback, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import billService from '@api/v1/billService'
import userService from '@api/v1/userService'
import HtmlDiff from 'htmldiff-js'
import LoadingSpinner from '@components/common/LoadingSpinner'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * CC-53439: 'Unbilled Entries' is deliberately NOT here.
 *
 * That tab never had a data source — `billService` has no unbilled-entries method,
 * and the page passed it a literal `[]`, so it stated "All entries have been billed
 * or are on other pre-bills" on every pre-bill having asked nothing. The false claim
 * was fixed first (it said it wasn't loaded), but a tab whose only content is an
 * admission that it does not work still costs a click and some trust on a billing
 * screen. Jordan's call 2026-09-02: hide it until someone asks for the feature.
 *
 * To bring it back you need a real read, not just this string: add the service
 * method, then restore the tab and the `UnbilledEntriesTab` render branch from
 * `git show 7ccb4ec:src/pages/PreBillDetail.jsx`. The component is removed rather
 * than left unrendered — unreachable UI that looks finished is how the `[]` got
 * there in the first place.
 */
const TABS = ['Pre-Bill', 'Line Items', 'Held Entries', 'Version History']

// Markup colors — one per approver version (matching CB's color scheme)
const MARKUP_COLORS = [
  { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', del: 'text-blue-400', ins: 'text-blue-700', label: 'Current' },
  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', del: 'text-emerald-400', ins: 'text-emerald-700', label: 'Approver 2' },
  { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', del: 'text-purple-400', ins: 'text-purple-700', label: 'Approver 3' },
  { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', del: 'text-orange-400', ins: 'text-orange-700', label: 'Approver 4' },
  { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', del: 'text-rose-400', ins: 'text-rose-700', label: 'Approver 5' },
]

// ---------------------------------------------------------------------------
// Reducer — tracks all edits with color/version info
// ---------------------------------------------------------------------------

function editReducer(state, action) {
  switch (action.type) {
    case 'EDIT_FIELD': {
      const key = `${action.entryId}:${action.field}`
      const existing = state.edits[key]
      return {
        ...state,
        edits: {
          ...state.edits,
          [key]: {
            entryId: action.entryId,
            field: action.field,
            originalValue: existing?.originalValue ?? action.originalValue,
            newValue: action.value,
            colorIndex: state.currentColorIndex,
            timestamp: Date.now(),
          },
        },
        dirty: true,
      }
    }
    case 'REVERT_ENTRY': {
      const next = { ...state.edits }
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${action.entryId}:`)) delete next[k]
      })
      return { ...state, edits: next, dirty: Object.keys(next).length > 0 }
    }
    case 'REVERT_FIELD': {
      const key = `${action.entryId}:${action.field}`
      const { [key]: _, ...rest } = state.edits
      return { ...state, edits: rest, dirty: Object.keys(rest).length > 0 }
    }
    case 'CLEAR':
      return { ...state, edits: {}, dirty: false }
    case 'MARK_SAVED': {
      // Move current edits to history (previous approver versions)
      const saved = Object.entries(state.edits).reduce((acc, [k, v]) => {
        acc[k] = { ...v, saved: true }
        return acc
      }, {})
      return { ...state, edits: {}, history: { ...state.history, ...saved }, dirty: false }
    }
    case 'HOLD_ENTRY':
      return { ...state, heldEntryIds: new Set([...state.heldEntryIds, action.entryId]) }
    case 'UNHOLD_ENTRY': {
      const next = new Set(state.heldEntryIds)
      next.delete(action.entryId)
      return { ...state, heldEntryIds: next }
    }
    case 'REMOVE_ENTRY':
      return { ...state, removedEntryIds: new Set([...state.removedEntryIds, action.entryId]) }
    case 'RESTORE_ENTRY': {
      const next = new Set(state.removedEntryIds)
      next.delete(action.entryId)
      return { ...state, removedEntryIds: next }
    }
    default:
      return state
  }
}

const initialEditState = {
  edits: {},
  history: {},
  dirty: false,
  currentColorIndex: 0,
  heldEntryIds: new Set(),
  removedEntryIds: new Set(),
}

// ---------------------------------------------------------------------------
// Utility components
// ---------------------------------------------------------------------------

const CurrencyValue = ({ amount, bold, className = '' }) => {
  if (amount == null) return <span className={`text-sm text-gray-500 ${className}`}>--</span>
  const formatted = `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return (
    <span className={`text-sm ${bold ? 'font-semibold text-gray-900 text-base' : 'text-gray-900'} ${className}`}>
      {formatted}
    </span>
  )
}

const StatusBadge = ({ status, isPosted }) => {
  if (isPosted) {
    return <span className="text-xs font-medium px-2.5 py-0.5 rounded-full text-emerald-700 bg-emerald-50">Posted</span>
  }
  const s = (status || '').toLowerCase()
  if (s === 'approved') return <span className="text-xs font-medium px-2.5 py-0.5 rounded-full text-emerald-700 bg-emerald-50">Approved</span>
  if (s === 'rejected') return <span className="text-xs font-medium px-2.5 py-0.5 rounded-full text-red-700 bg-red-50">Rejected</span>
  return <span className="text-xs font-medium px-2.5 py-0.5 rounded-full text-amber-700 bg-amber-50">Pending</span>
}

/**
 * The surface an UNRESOLVED read gets — never the empty state (CC-53132).
 *
 * Every panel on this page used to describe a read that had not happened as an
 * absence: "No time entries on this bill", "No held entries", "No version
 * history yet", "Pre-bill not found". react-query's `isLoading` is
 * `isPending && isFetching`, so it is FALSE for a query that is pending but not
 * in flight — above all a PAUSED one (default `networkMode: 'online'` while
 * `onlineManager` believes the browser is offline). `queryFn` is never called,
 * nothing throws, nothing logs, `data` stays `undefined`, and the panel states,
 * confidently and indefinitely, that a bill under review carries no time.
 *
 * On a pre-bill that is a billing claim, not a rendering detail: "this bill has
 * no time entries" is what an approver acts on. So the two failing states get a
 * panel that says in as many words that this is NOT an empty bill, `role="alert"`
 * so it is announced rather than silently swapped in, and a retry — because a
 * paused query never resolves on its own and a user with no way out concludes
 * the bill really is empty.
 *
 * `subject` is what could not be read ("the entries on this bill"); `notEmpty`
 * is the sentence that names the wrong conclusion the user would otherwise draw.
 */
const UnresolvedReadNotice = ({ state, subject, notEmpty, error, onRetry, className = '' }) => {
  const blocked = state === 'blocked'
  const tone = blocked
    ? { box: 'border-amber-200 bg-amber-50 text-amber-900', body: 'text-amber-800', btn: 'border-amber-300 text-amber-900 hover:bg-amber-100' }
    : { box: 'border-red-100 bg-red-50 text-red-700', body: 'text-red-700', btn: 'border-red-200 text-red-700 hover:bg-red-100' }

  return (
    <div role="alert" className={`rounded-xl border p-6 text-sm ${tone.box} ${className}`}>
      <p className="font-medium">
        {blocked
          ? `Can’t reach the server, so we haven’t loaded ${subject}.`
          : `We couldn’t load ${subject}${error?.message ? `: ${error.message}` : '.'}`}
      </p>
      <p className={`mt-1 ${tone.body}`}>
        {notEmpty}
        {blocked ? ' Your connection looks offline; check it and try again.' : ''}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={`mt-3 rounded-lg border bg-white px-3 py-1.5 text-xs font-medium ${tone.btn}`}
        >
          Try again
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editable cell with color markup
// ---------------------------------------------------------------------------

const EditableField = ({
  value,
  entryId,
  field,
  editState,
  dispatch,
  isEditing,
  showMarkup,
  format = 'text',
  className = '',
}) => {
  const [localEditing, setLocalEditing] = useState(false)
  const inputRef = useRef(null)

  const editKey = `${entryId}:${field}`
  const edit = editState.edits[editKey]
  const historyEdit = editState.history[editKey]
  const hasCurrentEdit = edit && edit.newValue !== edit.originalValue
  const hasHistoryEdit = historyEdit && historyEdit.newValue !== historyEdit.originalValue

  const displayValue = hasCurrentEdit ? edit.newValue : value
  const color = hasCurrentEdit ? MARKUP_COLORS[edit.colorIndex % MARKUP_COLORS.length] : null
  const historyColor = hasHistoryEdit ? MARKUP_COLORS[historyEdit.colorIndex % MARKUP_COLORS.length] : null

  const formatValue = (v) => {
    if (v == null || v === '') return '--'
    if (format === 'hours') return Number(v).toFixed(1)
    if (format === 'currency') return `$${Number(v).toFixed(2)}`
    if (format === 'number') return Number(v).toFixed(2)
    return String(v)
  }

  if (localEditing && isEditing) {
    return (
      <input
        ref={inputRef}
        type={format === 'text' ? 'text' : 'number'}
        step={format === 'hours' ? '0.1' : '0.01'}
        className={`w-full text-sm border border-cb-accent rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-cb-accent ${className}`}
        defaultValue={displayValue}
        autoFocus
        onBlur={(e) => {
          const raw = e.target.value
          const newVal = format === 'text' ? raw : parseFloat(raw)
          if (newVal !== value && raw !== '') {
            dispatch({ type: 'EDIT_FIELD', entryId, field, value: newVal, originalValue: value })
          }
          setLocalEditing(false)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur()
          if (e.key === 'Escape') setLocalEditing(false)
        }}
      />
    )
  }

  // Show markup mode — colored diffs
  if (showMarkup && (hasCurrentEdit || hasHistoryEdit)) {
    const activeEdit = hasCurrentEdit ? edit : historyEdit
    const activeColor = hasCurrentEdit ? color : historyColor
    return (
      <span
        className={`${isEditing ? 'cursor-pointer' : ''} ${className}`}
        onClick={() => isEditing && setLocalEditing(true)}
      >
        <span className={`line-through ${activeColor.del} text-sm`}>
          {formatValue(activeEdit.originalValue)}
        </span>
        <span className={`ml-1.5 font-medium ${activeColor.ins} text-sm`}>
          {formatValue(activeEdit.newValue)}
        </span>
      </span>
    )
  }

  // Non-markup mode — just show current value, editable if in edit mode
  if (hasCurrentEdit && !showMarkup) {
    return (
      <span
        className={`${isEditing ? 'cursor-pointer' : ''} font-medium text-sm ${className}`}
        onClick={() => isEditing && setLocalEditing(true)}
      >
        {formatValue(displayValue)}
      </span>
    )
  }

  return (
    <span
      className={`text-sm ${isEditing ? 'cursor-pointer hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 rounded px-1 -mx-1' : ''} ${className}`}
      onClick={() => isEditing && setLocalEditing(true)}
    >
      {formatValue(value)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

const Toolbar = ({
  bill,
  isEditing,
  setIsEditing,
  showMarkup,
  setShowMarkup,
  showActions,
  setShowActions,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onPrint,
  editState,
  onSaveAll,
  onDiscardAll,
  saving,
}) => {
  const actionsRef = useRef(null)

  return (
    <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-2.5 mb-4 shadow-sm">
      {/* Left side — Edit toggle + bill info */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsEditing(!isEditing)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isEditing
              ? 'bg-cb-accent text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
          </svg>
          {isEditing ? 'Editing' : 'Edit'}
        </button>

        {editState.dirty && (
          <div className="flex items-center gap-2 ml-1">
            <button
              onClick={onSaveAll}
              disabled={saving}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onDiscardAll}
              className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-xs font-medium"
            >
              Discard
            </button>
          </div>
        )}
      </div>

      {/* Center — Prev/Next navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          disabled={!hasPrev}
          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous pre-bill"
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span className="text-sm text-gray-500 min-w-[120px] text-center">
          {bill.invoiceNumber || bill.name || 'Pre-Bill'}
        </span>
        <button
          onClick={onNext}
          disabled={!hasNext}
          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next pre-bill"
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>

      {/* Right side — Show Markup + Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowMarkup(!showMarkup)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showMarkup
              ? 'bg-amber-100 text-amber-800'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title="Toggle markup visibility"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Markup
        </button>

        {/* Actions dropdown */}
        <div className="relative" ref={actionsRef}>
          <button
            onClick={() => setShowActions(!showActions)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Actions
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
          {showActions && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
              <button
                onClick={() => { onPrint(); setShowActions(false) }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                </svg>
                Print Invoice
              </button>
              <button
                onClick={() => setShowActions(false)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.75V16.5L12 14.25 7.5 16.5V3.75m9 0H18A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6A2.25 2.25 0 016 3.75h1.5m9 0h-9" />
                </svg>
                Notes
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => setShowActions(false)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Version History
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invoice Preview — editable when in edit mode, markup-aware
// ---------------------------------------------------------------------------

const InvoicePreview = ({ bill, lineItems, isEditing, showMarkup, editState, dispatch }) => {
  const timeEntries = (lineItems || []).filter(
    (e) => !editState.removedEntryIds.has(e.id) && !editState.heldEntryIds.has(e.id) &&
      ((e.type || '').toLowerCase().includes('time') || e.hours > 0)
  )
  const expenseEntries = (lineItems || []).filter(
    (e) => !editState.removedEntryIds.has(e.id) && !editState.heldEntryIds.has(e.id) &&
      !((e.type || '').toLowerCase().includes('time') || e.hours > 0)
  )

  // Compute live totals from edits
  const computedFeeTotal = timeEntries.reduce((sum, e) => {
    const hEdit = editState.edits[`${e.id}:hours`]
    const rEdit = editState.edits[`${e.id}:rate`]
    const h = hEdit ? hEdit.newValue : (e.hours || 0)
    const r = rEdit ? rEdit.newValue : (e.actualPrice || e.billableValue || 0)
    return sum + h * r
  }, 0)

  const computedExpenseTotal = expenseEntries.reduce((sum, e) => {
    const aEdit = editState.edits[`${e.id}:amount`]
    return sum + (aEdit ? aEdit.newValue : (e.billableValue || 0))
  }, 0)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-4xl mx-auto print:shadow-none print:border-0 print:p-0">
      {/* Invoice Header */}
      <div className="border-b-2 border-cb-navy pb-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            {bill.company && (
              <h2 className="text-xl font-display text-cb-navy">{bill.company}</h2>
            )}
          </div>
          <div className="text-right">
            <h1 className="text-2xl font-display text-cb-navy">INVOICE</h1>
            <p className="text-sm text-gray-600 mt-1">#{bill.invoiceNumber || bill.name}</p>
            {bill.issueDate && (
              <p className="text-sm text-gray-500">{dayjs(bill.issueDate).format('MMMM D, YYYY')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Bill To / Matter */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          {bill.billToContact && (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Bill To</p>
              <p className="text-sm text-gray-900">{bill.billToContact}</p>
            </>
          )}
        </div>
        <div>
          {bill.matter && (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Matter</p>
              <p className="text-sm text-gray-900">{bill.matter}</p>
            </>
          )}
          {bill.attorney && (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 mt-3">Attorney</p>
              <p className="text-sm text-gray-900">{bill.attorney}</p>
            </>
          )}
          {bill.startDate && bill.endDate && (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 mt-3">Period</p>
              <p className="text-sm text-gray-900">
                {dayjs(bill.startDate).format('MMM D, YYYY')} — {dayjs(bill.endDate).format('MMM D, YYYY')}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Time Entries — Editable */}
      {timeEntries.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Professional Services</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-gray-500 font-medium w-20">Date</th>
                <th className="text-left py-2 text-gray-500 font-medium w-28">Professional</th>
                <th className="text-left py-2 text-gray-500 font-medium">Description</th>
                <th className="text-right py-2 text-gray-500 font-medium w-16">Hours</th>
                <th className="text-right py-2 text-gray-500 font-medium w-20">Rate</th>
                <th className="text-right py-2 text-gray-500 font-medium w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {timeEntries.map((e, i) => {
                const hEdit = editState.edits[`${e.id}:hours`]
                const rEdit = editState.edits[`${e.id}:rate`]
                const hours = hEdit ? hEdit.newValue : (e.hours || 0)
                const rate = rEdit ? rEdit.newValue : (e.actualPrice || e.billableValue || 0)
                const total = hours * rate

                return (
                  <tr key={e.id || i} className="border-b border-gray-50 group">
                    <td className="py-2 text-gray-600">{e.date ? dayjs(e.date).format('MM/DD') : ''}</td>
                    <td className="py-2 text-gray-600">{e.timekeeper || ''}</td>
                    <td className="py-2 text-gray-700">
                      <EditableField
                        value={e.description}
                        entryId={e.id}
                        field="description"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="text"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <EditableField
                        value={e.hours}
                        entryId={e.id}
                        field="hours"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="hours"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <EditableField
                        value={e.actualPrice || e.billableValue}
                        entryId={e.id}
                        field="rate"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="currency"
                      />
                    </td>
                    <td className="py-2 text-right font-medium text-gray-900">
                      ${total.toFixed(2)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <td colSpan={3} className="py-2 text-right font-medium text-gray-700">Total Professional Services</td>
                <td className="py-2 text-right font-medium text-gray-700">
                  {timeEntries.reduce((s, e) => {
                    const hEdit = editState.edits[`${e.id}:hours`]
                    return s + (hEdit ? hEdit.newValue : (e.hours || 0))
                  }, 0).toFixed(1)}
                </td>
                <td></td>
                <td className="py-2 text-right font-semibold text-gray-900">
                  ${computedFeeTotal.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Expenses — Editable */}
      {expenseEntries.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Expenses</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-gray-500 font-medium w-20">Date</th>
                <th className="text-left py-2 text-gray-500 font-medium">Description</th>
                <th className="text-right py-2 text-gray-500 font-medium w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {expenseEntries.map((e, i) => (
                <tr key={e.id || i} className="border-b border-gray-50">
                  <td className="py-2 text-gray-600">{e.date ? dayjs(e.date).format('MM/DD') : ''}</td>
                  <td className="py-2 text-gray-700">
                    <EditableField
                      value={e.description || e.name}
                      entryId={e.id}
                      field="description"
                      editState={editState}
                      dispatch={dispatch}
                      isEditing={isEditing}
                      showMarkup={showMarkup}
                      format="text"
                    />
                  </td>
                  <td className="py-2 text-right">
                    <EditableField
                      value={e.billableValue}
                      entryId={e.id}
                      field="amount"
                      editState={editState}
                      dispatch={dispatch}
                      isEditing={isEditing}
                      showMarkup={showMarkup}
                      format="currency"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Total Expenses</td>
                <td className="py-2 text-right font-semibold text-gray-900">
                  ${computedExpenseTotal.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Totals */}
      <div className="border-t-2 border-cb-navy pt-4 mt-6">
        <div className="flex flex-col items-end gap-1">
          <div className="flex justify-between w-64">
            <span className="text-sm text-gray-600">Fees</span>
            <span className="text-sm text-gray-900">${computedFeeTotal.toFixed(2)}</span>
          </div>
          {computedExpenseTotal > 0 && (
            <div className="flex justify-between w-64">
              <span className="text-sm text-gray-600">Expenses</span>
              <span className="text-sm text-gray-900">${computedExpenseTotal.toFixed(2)}</span>
            </div>
          )}
          {bill.discountTotal != null && bill.discountTotal !== 0 && (
            <div className="flex justify-between w-64">
              <span className="text-sm text-gray-600">
                Discount{bill.discountPercent ? ` (${bill.discountPercent}%)` : ''}
              </span>
              <span className="text-sm text-red-600">-${Number(bill.discountTotal).toFixed(2)}</span>
            </div>
          )}
          {bill.tax != null && bill.tax !== 0 && (
            <div className="flex justify-between w-64">
              <span className="text-sm text-gray-600">Tax</span>
              <span className="text-sm text-gray-900">${Number(bill.tax).toFixed(2)}</span>
            </div>
          )}
          {bill.interest != null && bill.interest !== 0 && (
            <div className="flex justify-between w-64">
              <span className="text-sm text-gray-600">Interest</span>
              <span className="text-sm text-gray-900">${Number(bill.interest).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between w-64 border-t border-gray-300 pt-2 mt-1">
            <span className="text-base font-semibold text-cb-navy">Total Due</span>
            <span className="text-base font-semibold text-cb-navy">
              ${(computedFeeTotal + computedExpenseTotal - (bill.discountTotal || 0) + (bill.tax || 0) + (bill.interest || 0)).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Terms */}
      {bill.terms && (
        <div className="mt-8 pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Terms</p>
          <p className="text-sm text-gray-600">{bill.terms}</p>
        </div>
      )}

      {/* Markup Legend */}
      {showMarkup && Object.keys({ ...editState.edits, ...editState.history }).length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Edit Legend</p>
          <div className="flex gap-4">
            {MARKUP_COLORS.slice(0, 1).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`inline-block w-3 h-3 rounded-full ${c.bg} ${c.border} border`} />
                <span className="text-xs text-gray-500">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Line Items Tab — with Hold/Remove/Restore actions
// ---------------------------------------------------------------------------

const LineItemsTab = ({
  timeEntries,
  expenses,
  isEditing,
  showMarkup,
  editState,
  dispatch,
  isPosted,
  onSaveEntry,
  saving,
  lineItemsState,
  lineItemsResolved,
  lineItemsError,
  onRetryLineItems,
}) => {
  const visibleTime = timeEntries.filter(
    (e) => !editState.removedEntryIds.has(e.id) && !editState.heldEntryIds.has(e.id)
  )
  const visibleExpenses = expenses.filter(
    (e) => !editState.removedEntryIds.has(e.id) && !editState.heldEntryIds.has(e.id)
  )

  /*
   * `idle` rides along with `loading` here, deliberately.
   *
   * The line-items query is `enabled: !!id`, so it CAN reach `idle` — but only
   * when the route has no `:id`, and the bill query is gated on the very same
   * `!!id` and returns its own "no pre-bill selected" screen before this tab is
   * ever rendered. So `idle` is unreachable at this render site today. It is
   * folded into the spinner rather than left unhandled because an unhandled
   * fifth state renders NOTHING, and a blank panel is the failure mode this
   * whole ticket exists to remove — the first cut of the catalog fix shipped
   * exactly that. A spinner can never become permanent here for the same reason
   * it is unreachable: no `id`, no tab.
   */
  const stillReading = lineItemsState === 'loading' || lineItemsState === 'idle'
  // `lineItemsResolved` is NOT `state === 'ready'`: the bill payload can carry
  // `lineItems` itself, and `allLineItems` falls back to it. When it does, the
  // rows on screen came from a read that DID complete, so an empty section is a
  // real answer and must keep reading as one. See the memo in the page body.
  const unresolved = !lineItemsResolved && !stillReading

  const EntryActions = ({ entry }) => {
    if (isPosted) return null
    return (
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => dispatch({ type: 'HOLD_ENTRY', entryId: entry.id })}
          className="text-xs text-amber-600 hover:text-amber-700 font-medium px-1.5 py-0.5 rounded hover:bg-amber-50"
          title="Hold entry"
        >
          Hold
        </button>
        <button
          onClick={() => dispatch({ type: 'REMOVE_ENTRY', entryId: entry.id })}
          className="text-xs text-red-500 hover:text-red-600 font-medium px-1.5 py-0.5 rounded hover:bg-red-50"
          title="Remove entry"
        >
          Remove
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Time Entries */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            Time Entries
            {/* The count is gated on a COMPLETED read as well as on being non-zero.
                Without the gate an unresolved read renders no badge only by luck —
                because `0 > 0` is false — and the moment someone changes this to
                always render the number, an unread bill starts reporting "(0)". */}
            {lineItemsResolved && visibleTime.length > 0 && <span className="text-gray-500 font-normal ml-1">({visibleTime.length})</span>}
          </h3>
        </div>
        {stillReading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner message="Loading entries..." />
          </div>
        ) : unresolved ? (
          <div className="p-6">
            <UnresolvedReadNotice
              state={lineItemsState}
              subject="the time on this bill"
              notEmpty="This is not a bill with no time entries — the entries were never read, so nothing here describes what is billable."
              error={lineItemsError}
              onRetry={onRetryLineItems}
            />
          </div>
        ) : visibleTime.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-500">No time entries on this bill</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Timekeeper</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">Description</th>
                <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                {!isPosted && <th className="px-5 py-2.5 w-28"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visibleTime.map((entry, i) => {
                const hEdit = editState.edits[`${entry.id}:hours`]
                const rEdit = editState.edits[`${entry.id}:rate`]
                const hours = hEdit ? hEdit.newValue : (entry.hours || 0)
                const rate = rEdit ? rEdit.newValue : (entry.actualPrice || entry.billableValue || 0)
                const total = hours * rate
                const hasEdits = Object.keys(editState.edits).some((k) => k.startsWith(`${entry.id}:`))

                return (
                  <tr key={entry.id || i} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-5 py-3 text-sm text-gray-600">
                      {entry.date ? dayjs(entry.date).format('MMM D') : '--'}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">{entry.timekeeper || '--'}</td>
                    <td className="px-5 py-3 max-w-xs">
                      <EditableField
                        value={entry.description}
                        entryId={entry.id}
                        field="description"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="text"
                        className="text-gray-600"
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <EditableField
                        value={entry.hours}
                        entryId={entry.id}
                        field="hours"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="hours"
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <EditableField
                        value={entry.actualPrice || entry.billableValue}
                        entryId={entry.id}
                        field="rate"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="currency"
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="text-sm font-medium text-gray-900">${total.toFixed(2)}</span>
                    </td>
                    {!isPosted && (
                      <td className="px-5 py-3 text-right">
                        {hasEdits ? (
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => onSaveEntry(entry.id)}
                              disabled={saving}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => dispatch({ type: 'REVERT_ENTRY', entryId: entry.id })}
                              className="text-xs text-gray-500 hover:text-gray-600 ml-1"
                            >
                              Undo
                            </button>
                          </div>
                        ) : (
                          <EntryActions entry={entry} />
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 bg-gray-50/50">
                <td colSpan={3} className="px-5 py-2.5">
                  <span className="text-sm font-semibold text-gray-700">Total</span>
                </td>
                <td className="px-5 py-2.5 text-right">
                  <span className="text-sm font-semibold text-gray-900">
                    {visibleTime.reduce((sum, e) => {
                      const hEdit = editState.edits[`${e.id}:hours`]
                      return sum + (hEdit ? hEdit.newValue : (e.hours ?? 0))
                    }, 0).toFixed(1)}
                  </span>
                </td>
                <td className="px-5 py-2.5"></td>
                <td className="px-5 py-2.5 text-right">
                  <span className="text-sm font-semibold text-gray-900">
                    ${visibleTime.reduce((sum, e) => {
                      const hEdit = editState.edits[`${e.id}:hours`]
                      const rEdit = editState.edits[`${e.id}:rate`]
                      const h = hEdit ? hEdit.newValue : (e.hours ?? 0)
                      const r = rEdit ? rEdit.newValue : (e.actualPrice ?? e.billableValue ?? 0)
                      return sum + h * r
                    }, 0).toFixed(2)}
                  </span>
                </td>
                {!isPosted && <td></td>}
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Expenses */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-gray-700">
            Expenses
            {lineItemsResolved && visibleExpenses.length > 0 && <span className="text-gray-500 font-normal ml-1">({visibleExpenses.length})</span>}
          </h3>
        </div>
        {stillReading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner message="Loading expenses..." />
          </div>
        ) : unresolved ? (
          <div className="p-6">
            <UnresolvedReadNotice
              state={lineItemsState}
              subject="the expenses on this bill"
              notEmpty="This is not a bill with no expenses — the same read that carries the time entries carries these, and it never completed."
              error={lineItemsError}
              onRetry={onRetryLineItems}
            />
          </div>
        ) : visibleExpenses.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-500">No expenses on this bill</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                {!isPosted && <th className="px-5 py-2.5 w-28"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visibleExpenses.map((entry, i) => {
                const hasEdits = Object.keys(editState.edits).some((k) => k.startsWith(`${entry.id}:`))
                return (
                  <tr key={entry.id || i} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-5 py-3 text-sm text-gray-600">
                      {entry.date ? dayjs(entry.date).format('MMM D') : '--'}
                    </td>
                    <td className="px-5 py-3 max-w-md">
                      <EditableField
                        value={entry.description || entry.name}
                        entryId={entry.id}
                        field="description"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="text"
                        className="text-gray-600"
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <EditableField
                        value={entry.billableValue}
                        entryId={entry.id}
                        field="amount"
                        editState={editState}
                        dispatch={dispatch}
                        isEditing={isEditing}
                        showMarkup={showMarkup}
                        format="currency"
                      />
                    </td>
                    {!isPosted && (
                      <td className="px-5 py-3 text-right">
                        {hasEdits ? (
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => onSaveEntry(entry.id)}
                              disabled={saving}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => dispatch({ type: 'REVERT_ENTRY', entryId: entry.id })}
                              className="text-xs text-gray-500 hover:text-gray-600 ml-1"
                            >
                              Undo
                            </button>
                          </div>
                        ) : (
                          <EntryActions entry={entry} />
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 bg-gray-50/50">
                <td className="px-5 py-2.5"><span className="text-sm font-semibold text-gray-700">Total</span></td>
                <td></td>
                <td className="px-5 py-2.5 text-right">
                  <span className="text-sm font-semibold text-gray-900">
                    ${visibleExpenses.reduce((sum, e) => {
                      const aEdit = editState.edits[`${e.id}:amount`]
                      return sum + (aEdit ? aEdit.newValue : (e.billableValue ?? 0))
                    }, 0).toFixed(2)}
                  </span>
                </td>
                {!isPosted && <td></td>}
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Held Entries Tab
// ---------------------------------------------------------------------------

const HeldEntriesTab = ({
  allLineItems,
  editState,
  dispatch,
  heldFromApi,
  lineItemsState,
  lineItemsResolved,
  lineItemsError,
  onRetryLineItems,
}) => {
  // Combine API-held entries + locally held entries
  const heldEntries = [
    ...(heldFromApi || []),
    ...allLineItems.filter((e) => editState.heldEntryIds.has(e.id)),
  ]

  /*
   * `heldFromApi` is `allLineItems.filter(e => e.isHeld)`, so it is empty both
   * when nothing is held AND when the line-items read never happened. "No held
   * entries" is the more dangerous of the two readings on a pre-bill: an
   * approver who believes nothing was held approves the bill as it stands.
   *
   * The locally-held ids are the user's own actions this session and are known
   * regardless of the read, so they still show — but they cannot stand in for
   * the server's list, which is why the notice appears even when the local set
   * is non-empty.
   *
   * `idle` folds into the spinner for the reason given in `LineItemsTab`: it is
   * unreachable behind the bill gate, and an unhandled state renders nothing.
   */
  const stillReading = lineItemsState === 'loading' || lineItemsState === 'idle'
  const unresolved = !lineItemsResolved && !stillReading

  if (stillReading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 flex justify-center">
        <LoadingSpinner message="Loading held entries..." />
      </div>
    )
  }

  if (unresolved) {
    return (
      <UnresolvedReadNotice
        state={lineItemsState}
        subject="the entries on this bill"
        notEmpty="This is not a bill with nothing held — the entries were never read, so we cannot tell you what an approver held back."
        error={lineItemsError}
        onRetry={onRetryLineItems}
      />
    )
  }

  if (heldEntries.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
        </svg>
        <p className="text-sm text-gray-500">No held entries</p>
        <p className="text-xs text-gray-300 mt-1">Entries held from this bill will appear here</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-amber-50/50">
        <h3 className="text-sm font-semibold text-amber-700">
          Held Entries
          <span className="text-amber-500 font-normal ml-1">({heldEntries.length})</span>
        </h3>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
            <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Timekeeper</th>
            <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hours</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
            <th className="px-5 py-2.5 w-24"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {heldEntries.map((entry, i) => (
            <tr key={entry.id || i} className="hover:bg-gray-50/50">
              <td className="px-5 py-3 text-sm text-gray-600">
                {entry.date ? dayjs(entry.date).format('MMM D') : '--'}
              </td>
              <td className="px-5 py-3 text-sm text-gray-600">{entry.timekeeper || '--'}</td>
              <td className="px-5 py-3 text-sm text-gray-600 max-w-xs truncate">{entry.description || '--'}</td>
              <td className="px-5 py-3 text-sm text-right text-gray-600">{entry.hours?.toFixed(1) || '--'}</td>
              <td className="px-5 py-3 text-sm text-right font-medium text-gray-900">
                ${(entry.billableValue || 0).toFixed(2)}
              </td>
              <td className="px-5 py-3 text-right">
                <button
                  onClick={() => dispatch({ type: 'UNHOLD_ENTRY', entryId: entry.id })}
                  className="text-xs text-cb-accent-text hover:text-cb-accent-text/80 font-medium"
                >
                  Restore
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Version History Tab — snapshot comparison timeline
// ---------------------------------------------------------------------------

const VersionHistoryTab = ({ snapshotData, snapshotState, snapshotError, onRetrySnapshot }) => {
  const history = snapshotData?.history || {}
  const userNames = history.userNames || []
  const colors = history.colors || []
  const numberOfVersions = history.numberOfVersions || 0
  const versionDates = history.versionDates || []

  /*
   * `numberOfVersions` is `snapshotData?.history?.numberOfVersions || 0`, so it
   * is 0 both when no approver has ever edited this bill and when the snapshot
   * read never happened — and this tab had no loading state at all, so an
   * in-flight read ALSO rendered "No version history yet". On a pre-bill that
   * says nobody has touched the figures, which is the one thing an approver is
   * here to check.
   *
   * `idle` folds into the spinner: the query is `enabled: !!id` and the bill
   * gate, on the same `!!id`, returns before this tab renders — so it is
   * unreachable, and an unhandled state renders a blank tab.
   */
  if (snapshotState === 'loading' || snapshotState === 'idle') {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 flex justify-center">
        <LoadingSpinner message="Loading version history..." />
      </div>
    )
  }

  if (snapshotState === 'blocked' || snapshotState === 'failed') {
    return (
      <UnresolvedReadNotice
        state={snapshotState}
        subject="this pre-bill’s version history"
        notEmpty="This is not a pre-bill nobody has edited — the approval history was never read, so no version of these figures has been compared."
        error={snapshotError}
        onRetry={onRetrySnapshot}
      />
    )
  }

  if (numberOfVersions === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-gray-500">No version history yet</p>
        <p className="text-xs text-gray-500 mt-1">Versions are created when approvers save changes</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Approval Timeline</h3>
      <div className="space-y-4">
        {userNames.map((name, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colors[i] || '#6b7280' }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{name}</p>
              <p className="text-xs text-gray-500">
                Version {i + 1}
                {versionDates[i] && ` — ${versionDates[i]}`}
              </p>
            </div>
            <div className="flex-shrink-0">
              <span
                className="inline-block w-4 h-4 rounded"
                style={{ backgroundColor: colors[i] || '#6b7280' }}
                title={`${name}'s changes shown in this color`}
              />
            </div>
          </div>
        ))}
      </div>
      {snapshotData?.userName && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Your edits will appear in{' '}
            <span
              className="font-semibold"
              style={{ color: snapshotData.userColor || '#3b82f6' }}
            >
              this color
            </span>
          </p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Outside Approvals Panel
// ---------------------------------------------------------------------------

const OutsideApprovalsBanner = ({ approvalData, snapshotData, snapshotResolved }) => {
  const outsideApprove = approvalData?.outsideApprove
  if (outsideApprove !== 'True' && outsideApprove !== true) return null

  const history = snapshotData?.history || {}
  const userNames = history.userNames || []
  const numberOfVersions = history.numberOfVersions || 0

  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-4 flex items-center gap-3">
      <svg className="w-5 h-5 text-indigo-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
      <div className="flex-1">
        <p className="text-sm font-medium text-indigo-800">Outside Approval Required</p>
        {/* Both halves of this ternary are claims drawn from the snapshot read:
            the first is a COUNT of approvers, the second asserts that nobody has
            reviewed yet. An unresolved read produced the second one — "waiting
            for outside approvers" when we had not asked whether they had already
            signed off. Say which it is only once the read has completed. */}
        <p className="text-xs text-indigo-600 mt-0.5">
          {!snapshotResolved
            ? 'We haven’t been able to read who has reviewed this pre-bill'
            : numberOfVersions > 0
              ? `${userNames.length} of required approvers have reviewed`
              : 'Waiting for outside approvers to review this pre-bill'}
        </p>
      </div>
      {approvalData?.canOverride && (
        <span className="text-xs text-indigo-500">You can override</span>
      )}
    </div>
  )
}

const PreBillDetail = () => {
  useDocumentTitle('Pre-Bill Detail')
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('Pre-Bill')
  const [isEditing, setIsEditing] = useState(false)
  const [showMarkup, setShowMarkup] = useState(true)
  const [showActions, setShowActions] = useState(false)
  const [editState, dispatch] = useReducer(editReducer, initialEditState)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [userSearchText, setUserSearchText] = useState('')
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const userDropdownRef = useRef(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Load all users upfront for dropdown with client-side filter
  const allUsersQuery = useQuery({
    queryKey: ['allUsers'],
    queryFn: async () => {
      const result = await userService.getUsers({ limit: 500 })
      return (result?.data || result || []).map((u) => ({
        id: u.id,
        label: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      }))
    },
    staleTime: 300000,
  })
  const { data: allUsers } = allUsersQuery
  // No `enabled` option on this query, so `idle` is unreachable — it is folded
  // into the loading branch at the render site rather than left unhandled.
  const allUsersState = useReadState('prebill-all-users', allUsersQuery)

  const filteredUsers = useMemo(() => {
    if (!allUsers) return []
    if (!userSearchText) return allUsers
    const q = userSearchText.toLowerCase()
    return allUsers.filter((u) => u.label.toLowerCase().includes(q))
  }, [allUsers, userSearchText])

  /*
   * CC-53132 — the reads on this page are gated on `readState`, not `isLoading`.
   *
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but not in flight. react-query pauses a query outright while
   * `onlineManager` believes the browser is offline (the default
   * `networkMode: 'online'`): `queryFn` is never called, nothing throws, nothing
   * is logged on either side, and `data` stays `undefined`. Every panel below
   * then rendered its empty state — "Pre-bill not found", "No time entries on
   * this bill", "No held entries", "No version history yet" — as a statement of
   * fact about a bill that had never been read.
   *
   * `useReadState` rather than the bare `readState`: it carries the permanent
   * blocked-read diagnostic, which is the only trace a paused read leaves
   * anywhere.
   */
  // Bill detail
  const billQuery = useQuery({
    queryKey: ['bill', id],
    queryFn: () => billService.getBill(id),
    enabled: !!id,
  })
  const { data: bill, error } = billQuery
  const billState = useReadState('prebill-detail', billQuery)

  // Line items
  const lineItemsQuery = useQuery({
    queryKey: ['billLineItems', id],
    queryFn: () => billService.getBillLineItems(id),
    enabled: !!id,
  })
  const { data: lineItemsData } = lineItemsQuery
  const lineItemsState = useReadState('prebill-line-items', lineItemsQuery)

  // Snapshot version history
  const snapshotQuery = useQuery({
    queryKey: ['snapshotHistory', id],
    queryFn: () => billService.getSnapshotHistory(id),
    enabled: !!id,
  })
  const { data: snapshotData } = snapshotQuery
  const snapshotState = useReadState('prebill-snapshot-history', snapshotQuery)
  const snapshotResolved = snapshotState === 'ready'

  // Approval info (current user permissions + outside approvals)
  const { data: approvalData } = useQuery({
    queryKey: ['approvalInfo', id],
    queryFn: () => billService.getApprovalInfo(id),
    enabled: !!id,
  })

  // User settings — pre-bill queue with action names (for dynamic approve button)
  const { data: userSettings } = useQuery({
    queryKey: ['userSettings', selectedUserId || approvalData?.userId],
    queryFn: () => billService.getUserSettings(
      selectedUserId || approvalData?.userId,
      { outsideApproval: approvalData?.outsideApprove === 'True', invoiceId: id }
    ),
    enabled: !!(selectedUserId || approvalData?.userId),
  })

  // Pre-bill list for prev/next navigation
  const { data: preBillsData } = useQuery({
    queryKey: ['preBills', 'nav'],
    queryFn: () => billService.getPreBills({ limit: 1000 }),
    staleTime: 60000,
  })

  // Server-rendered invoice HTML (current version)
  const previewQuery = useQuery({
    queryKey: ['billPreview', id],
    queryFn: async () => {
      const result = await billService.getBillPreview(id)
      const html = result?.html || result
      if (typeof html === 'string' && html.length > 50) return html
      return null
    },
    enabled: !!id,
    staleTime: 60000,
  })
  const { data: currentPreviewHtml } = previewQuery
  const previewState = useReadState('prebill-preview', previewQuery)

  // Snapshot HTMLs for markup diff — fetch version 0 (original) and latest version
  // Both use the same PrebillSnapshot Razor template, so diffing works cleanly
  const numVersions = snapshotData?.history?.numberOfVersions || 0
  const latestVersion = numVersions > 0 ? numVersions - 1 : -1

  const { data: snapshotV0Html } = useQuery({
    queryKey: ['billSnapshot', id, 0],
    queryFn: async () => {
      const result = await billService.getSnapshot(id, 0)
      return result?.html || result || null
    },
    enabled: !!id && showMarkup && numVersions >= 2,
    staleTime: 300000,
  })

  const { data: snapshotLatestHtml } = useQuery({
    queryKey: ['billSnapshot', id, latestVersion],
    queryFn: async () => {
      const result = await billService.getSnapshot(id, latestVersion)
      return result?.html || result || null
    },
    enabled: !!id && showMarkup && numVersions >= 2 && latestVersion > 0,
    staleTime: 300000,
  })

  // Build markup: when markup is ON, diff snapshot v0 vs latest snapshot (same template)
  // When markup is OFF, show the current live Invoice/Print preview
  const billPreviewHtml = useMemo(() => {
    if (!showMarkup) return currentPreviewHtml || null
    if (!snapshotV0Html || !snapshotLatestHtml) return currentPreviewHtml || null

    try {
      // Extract just the invoice container content from both snapshots
      const extractContent = (html) => {
        // Try invoiceContainer class first
        let match = html.match(/<div[^>]*class="[^"]*invoiceContainer[^"]*"[^>]*>([\s\S]*)/i)
        if (match) {
          // Take everything inside the container until the matching close
          const content = match[1]
          // Find the end — look for the closing div before </body>
          const bodyEnd = content.lastIndexOf('</body>')
          return bodyEnd > 0 ? content.substring(0, bodyEnd) : content
        }
        // Fallback: extract body
        match = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
        return match ? match[1] : html
      }

      const oldContent = extractContent(snapshotV0Html)
      const newContent = extractContent(snapshotLatestHtml)

      // Run HTML diff on the same-template content
      const diffedContent = HtmlDiff.execute(oldContent, newContent)

      // Apply version colors
      const colors = snapshotData?.history?.colors || []
      const markupColor = colors.length > 1 ? colors[colors.length - 1].trim() : '#dc2626'

      const markupStyles = `
        <style>
          ins.diffins, ins.diffmod { color: ${markupColor} !important; text-decoration: none; font-weight: bold; }
          del.diffdel, del.diffmod { color: ${markupColor} !important; text-decoration: line-through; opacity: 0.7; }
        </style>
      `

      // Inject diffed content back into the latest snapshot's HTML shell
      if (snapshotLatestHtml.match(/<body[^>]*>/i)) {
        return snapshotLatestHtml.replace(
          /(<body[^>]*>)([\s\S]*)(<\/body>)/i,
          (_, open, _body, close) => `${open}${markupStyles}${diffedContent}${close}`
        )
      }
      return markupStyles + diffedContent
    } catch {
      return currentPreviewHtml || null
    }
  }, [currentPreviewHtml, snapshotV0Html, snapshotLatestHtml, showMarkup, snapshotData])

  /*
   * This used to be `!billPreviewHtml && !!id` — "no HTML yet" spelled as
   * "loading", which conflated three different facts and rendered the same
   * spinner for all of them, forever:
   *
   *   - the read is genuinely in flight            → spinner (correct)
   *   - the read is PAUSED or it threw             → spinner (a lie; it will
   *     never resolve, and there is nothing to wait for)
   *   - the read completed and CB returned no
   *     usable invoice HTML                        → spinner (a lie; the answer
   *     arrived, and it was "there is no preview")
   *
   * The third case is why "Invoice preview unavailable" — copy that already
   * exists below — was unreachable: `previewLoading` was true in exactly the
   * situation that message was written for. `idle` folds into the spinner
   * because the bill gate on the same `!!id` returns first.
   */
  const previewLoading = !billPreviewHtml && (previewState === 'loading' || previewState === 'idle')
  const previewUnresolved = !billPreviewHtml && (previewState === 'blocked' || previewState === 'failed')

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ entryId, changes }) => billService.updateLineItem(id, entryId, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billLineItems', id] })
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
    },
  })

  const approveMutation = useMutation({
    mutationFn: (payload) => billService.approveBill(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
      queryClient.invalidateQueries({ queryKey: ['preBills'] })
    },
  })

  const postMutation = useMutation({
    mutationFn: () => billService.postBill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
      queryClient.invalidateQueries({ queryKey: ['preBills'] })
    },
  })

  // Derived data
  const allLineItems = useMemo(() => {
    if (lineItemsData) {
      return [...(lineItemsData.timeEntries || []), ...(lineItemsData.expenseEntries || [])]
    }
    return bill?.lineItems || []
  }, [lineItemsData, bill])

  /*
   * Whether the rows above describe the bill, or describe nothing.
   *
   * NOT simply `lineItemsState === 'ready'`. `allLineItems` has a second source:
   * when the dedicated read has produced nothing, it falls back to
   * `bill.lineItems`, which arrives on the bill payload. If that array is there,
   * a read DID complete and an empty section is a real answer — gating on the
   * line-items query alone would replace live data with a "we couldn't read
   * this" notice, which is its own kind of lie.
   *
   * `Array.isArray` and not a truthiness check: `bill.lineItems` being absent
   * from the payload is not the same as the bill carrying an empty array, and
   * the `|| []` above erases that difference the moment it runs.
   */
  const lineItemsResolved = lineItemsState === 'ready' || Array.isArray(bill?.lineItems)

  const timeEntries = useMemo(() => {
    if (lineItemsData?.timeEntries) return lineItemsData.timeEntries
    return allLineItems.filter(
      (item) => (item.type || '').toLowerCase().includes('time') || item.hours > 0
    )
  }, [lineItemsData, allLineItems])

  const expenses = useMemo(() => {
    if (lineItemsData?.expenseEntries) return lineItemsData.expenseEntries
    return allLineItems.filter(
      (item) => !(item.type || '').toLowerCase().includes('time') && !(item.hours > 0)
    )
  }, [lineItemsData, allLineItems])

  // Held entries from API (entries with isHeld = true)
  const heldFromApi = useMemo(
    () => allLineItems.filter((e) => e.isHeld),
    [allLineItems]
  )

  // Prev/Next navigation — prefer userSettings queue, fall back to preBills list
  const navBills = useMemo(() => {
    if (userSettings?.invoiceList?.length > 0) {
      return userSettings.invoiceList.map((invoiceId, i) => ({
        id: invoiceId,
        actionName: userSettings.actionNames?.[i] || 'Approve',
        matterName: userSettings.matterNames?.[i] || '',
        billNumber: userSettings.billNumbers?.[i] || '',
      }))
    }
    return (preBillsData?.bills || []).map((b) => ({ id: b.id, actionName: 'Approve' }))
  }, [userSettings, preBillsData])

  const { hasPrev, hasNext, prevId, nextId, currentIndex, billCount, actionName } = useMemo(() => {
    const idx = navBills.findIndex((b) => b.id === id)
    return {
      hasPrev: idx > 0,
      hasNext: idx >= 0 && idx < navBills.length - 1,
      prevId: idx > 0 ? navBills[idx - 1].id : null,
      nextId: idx < navBills.length - 1 ? navBills[idx + 1]?.id : null,
      currentIndex: idx >= 0 ? idx + 1 : 0,
      billCount: navBills.length,
      actionName: idx >= 0 ? (navBills[idx].actionName || 'Approve') : 'Approve',
    }
  }, [navBills, id])

  const canApproveForOthers = approvalData?.canApprove === true

  /**
   * CC-53433 — Approve is gated on the snapshot read having actually happened.
   *
   * `version` and `userName` both come from `snapshotData`, so an unresolved read
   * makes them `0` and `''`. cb-api's Step 3 guard was `version != null && userName`:
   * `0 != null` is true but `''` is falsy, so **`ApprovePrebillSnapshot` was skipped
   * entirely** while the approval succeeded and the route returned
   * `200 {success:true}`. The snapshot the NEXT approver reviews was never written,
   * nothing logged it, and the response was true about the approval and silent about
   * the omission.
   *
   * Gated rather than defaulted: dropping the `?? 0` / `|| ''` would make cb-api's
   * guard pass with `undefined` and send a genuinely malformed Step 3, which is worse
   * than skipping it. cb-api now also refuses this case with a 400 (see
   * `bills/routes.js`), so the two halves are independent — this one keeps the user
   * out of a dead end, that one protects every other caller.
   */
  const approveBlockedReason =
    snapshotState === 'blocked'
      ? 'Can’t reach the server to read this pre-bill’s approval history, so approving now would skip the snapshot the next approver reviews.'
      : snapshotState === 'failed'
        ? 'This pre-bill’s approval history could not be read, so approving now would skip the snapshot the next approver reviews.'
        : snapshotResolved
          ? null
          : 'Reading this pre-bill’s approval history…'

  // Handlers
  const handleApprove = () => {
    // Belt and braces: the button is disabled, but a keyboard or programmatic path
    // must not be able to send a payload we already know is incomplete.
    if (!snapshotResolved) return
    const version = snapshotData?.history?.numberOfVersions ?? 0
    const userName = snapshotData?.userName || ''
    const userId = approvalData?.userId || ''
    approveMutation.mutate({ action: 'approve', version, userName, userId })
  }

  const handleReject = () => {
    const comment = window.prompt('Reason for rejection:')
    if (comment !== null) {
      approveMutation.mutate({ action: 'reject', comment })
    }
  }

  const handlePost = () => {
    if (window.confirm('Post this bill to the ledger? This action cannot be undone.')) {
      postMutation.mutate()
    }
  }

  const handleSaveEntry = useCallback((entryId) => {
    const entryEdits = {}
    Object.entries(editState.edits).forEach(([key, edit]) => {
      if (edit.entryId === entryId) {
        entryEdits[edit.field] = edit.newValue
      }
    })
    if (Object.keys(entryEdits).length === 0) return

    // Map field names to API field names
    const changes = {}
    if (entryEdits.hours !== undefined) changes.quantity = entryEdits.hours
    if (entryEdits.rate !== undefined) changes.rate = entryEdits.rate
    if (entryEdits.description !== undefined) changes.description = entryEdits.description
    if (entryEdits.amount !== undefined) changes.quantity = entryEdits.amount

    updateMutation.mutate(
      { entryId, changes },
      { onSuccess: () => dispatch({ type: 'REVERT_ENTRY', entryId }) }
    )
  }, [editState.edits, updateMutation])

  const handleSaveAll = useCallback(async () => {
    // Group edits by entry
    const byEntry = {}
    Object.values(editState.edits).forEach((edit) => {
      if (!byEntry[edit.entryId]) byEntry[edit.entryId] = {}
      byEntry[edit.entryId][edit.field] = edit.newValue
    })

    for (const [entryId, fields] of Object.entries(byEntry)) {
      const changes = {}
      if (fields.hours !== undefined) changes.quantity = fields.hours
      if (fields.rate !== undefined) changes.rate = fields.rate
      if (fields.description !== undefined) changes.description = fields.description
      if (fields.amount !== undefined) changes.quantity = fields.amount
      await billService.updateLineItem(id, entryId, changes)
    }

    dispatch({ type: 'MARK_SAVED' })
    queryClient.invalidateQueries({ queryKey: ['billLineItems', id] })
    queryClient.invalidateQueries({ queryKey: ['bill', id] })
  }, [editState.edits, id, queryClient])

  const handleDiscardAll = () => dispatch({ type: 'CLEAR' })

  // Loading/error states — all five read states, in the order that keeps a
  // failure from being described as an absence.
  if (billState === 'loading') {
    return (
      <div className="max-w-7xl mx-auto p-12 flex justify-center">
        <LoadingSpinner message="Loading pre-bill..." />
      </div>
    )
  }

  /*
   * `idle` is NOT folded into the loading branch, and NOT treated as an error.
   *
   * This query is `enabled: !!id`, so `idle` is reachable and means exactly one
   * thing: the route gave us no pre-bill to open. Folding it into loading would
   * spin forever on a screen that is never going to ask anything; calling it a
   * failure would send the user chasing a server problem that does not exist.
   * It is the ordinary "not asked yet" state, so it says so and points at the
   * list — where a pre-bill can actually be chosen.
   */
  if (billState === 'idle') {
    return (
      <div className="max-w-7xl mx-auto p-12 text-center">
        <p className="text-sm text-gray-500 mb-4">No pre-bill selected</p>
        <button onClick={() => navigate('/pre-bills')} className="text-sm text-cb-accent-text hover:underline">
          Back to Pre-Bills
        </button>
      </div>
    )
  }

  /*
   * Paused: react-query never sent the request, so this page knows nothing about
   * this pre-bill. It must not fall through to "Pre-bill not found" — that is a
   * statement that the bill does not exist, and an approver who reads it goes
   * looking for a deleted invoice instead of checking their connection.
   */
  if (billState === 'blocked') {
    return (
      <div className="max-w-7xl mx-auto p-12">
        <UnresolvedReadNotice
          state="blocked"
          subject="this pre-bill"
          notEmpty="This is not a pre-bill that has been deleted or that you can’t see — no request was sent for it at all."
          onRetry={() => billQuery.refetch()}
        />
        <button onClick={() => navigate('/pre-bills')} className="mt-4 text-sm text-cb-accent-text hover:underline">
          Back to Pre-Bills
        </button>
      </div>
    )
  }

  /* Failed. The copy is unchanged — it was already honest about being a failure
     — but it now offers a retry as well as a way out, because a user whose only
     option is to leave the page has no way to find out the read would succeed on
     a second attempt. */
  /* Spec 148: a 404 is cb-api saying the pre-bill does not exist, so it falls
     through to "Pre-bill not found" below rather than "nothing was read". (Before
     spec 148 cb-api also answered 404 for every real bill past its first 500; it
     now reads bills by id, so a 404 means absent.) */
  if (error && error.response?.status !== 404) {
    return (
      <div role="alert" className="max-w-7xl mx-auto p-12 text-center">
        <p className="text-sm text-red-500 mb-4">Failed to load pre-bill: {error.message}</p>
        <p className="text-sm text-gray-500 mb-4">
          This is not a pre-bill that doesn’t exist — nothing was read.
        </p>
        <button
          type="button"
          onClick={() => billQuery.refetch()}
          className="mr-4 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
        >
          Try again
        </button>
        <button onClick={() => navigate('/pre-bills')} className="text-sm text-cb-accent-text hover:underline">
          Back to Pre-Bills
        </button>
      </div>
    )
  }

  // Ready, and CB genuinely returned nothing. Only NOW may the page say so.
  if (!bill) {
    return (
      <div className="max-w-7xl mx-auto p-12 text-center">
        <p className="text-sm text-gray-500">Pre-bill not found</p>
      </div>
    )
  }

  const status = bill.approvalStatus || bill.status || 'Draft'
  const isPosted = bill.isPosted

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back + Header */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/pre-bills')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to Pre-Bills
        </button>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-display text-gray-900">
                Invoice {bill.invoiceNumber || bill.name}
              </h1>
              <StatusBadge status={status} isPosted={isPosted} />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {bill.matter && <span className="mr-3">{bill.matter}</span>}
              {bill.attorney && <span className="mr-3">{bill.attorney}</span>}
              {bill.issueDate && (
                <span className="mr-3">{dayjs(bill.issueDate).format('MMM D, YYYY')}</span>
              )}
              {bill.invoiceTotal != null && (
                <span className="font-medium text-gray-900">
                  ${Number(bill.invoiceTotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              )}
            </p>
          </div>
          {/* Approval Actions */}
          {!isPosted && (
            <div className="flex items-start gap-2">
              <button
                onClick={handleReject}
                disabled={approveMutation.isPending}
                className="px-4 py-2 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50"
              >
                Reject
              </button>
              {/* CC-53433: Approve refuses while the snapshot read is unresolved,
                  and SAYS WHY. A disabled control with no reason is its own kind of
                  absence — the user cannot tell "not allowed" from "not yet".

                  `title` + `disabled:cursor-not-allowed` come from session
                  `vlad-demo-b0`'s PR #282, which implemented CC-53433 concurrently
                  and was merged over by #280. The cursor is what a mouse user gets
                  before they click; the title is what they get on hover. Both are
                  ADDITIVE to the visible reason below, never a substitute — a tooltip
                  is not an accessible name, and a cursor is not a message. */}
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending || !snapshotResolved}
                  aria-describedby={approveBlockedReason ? 'approve-blocked-reason' : undefined}
                  title={!snapshotResolved ? 'Waiting on this pre-bill’s approval history' : undefined}
                  className="px-4 py-2 bg-cb-accent text-white rounded-lg text-sm font-medium hover:bg-cb-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionName}
                </button>
                {approveBlockedReason && (
                  <p
                    id="approve-blocked-reason"
                    role="status"
                    className="max-w-xs text-right text-xs text-gray-500"
                  >
                    {approveBlockedReason}
                    {(snapshotState === 'blocked' || snapshotState === 'failed') && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => snapshotQuery.refetch()}
                          className="underline text-cb-accent-text hover:no-underline"
                        >
                          Try again
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={handlePost}
                disabled={postMutation.isPending}
                className="px-4 py-2 bg-cb-navy text-white rounded-lg text-sm font-medium hover:bg-cb-navy/90 disabled:opacity-50"
              >
                Post to Ledger
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter bar — matches CenterbaseWeb layout: Select Pre-bill | Client | Matter | Pre-Bill N/N | Markup | Actions | User */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-gray-200 rounded-xl px-4 py-3 mb-3 shadow-sm">
        {/* Bill selector dropdown */}
        {navBills.length > 1 && (
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">Select a pre-bill</label>
            <select
              value={id}
              onChange={(e) => navigate(`/pre-bills/${e.target.value}`)}
              className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-cb-accent max-w-[160px]"
            >
              {navBills.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.billNumber || b.id.substring(0, 8)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pre-Bill counter */}
        {billCount > 0 && (
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            Pre-Bill {currentIndex}/{billCount}
          </span>
        )}

        <div className="flex-1" />

        {/* User switcher — admin "approve for others" */}
        {canApproveForOthers && (
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">User</label>
            <div className="relative" ref={userDropdownRef}>
              <input
                type="text"
                placeholder="Search by User..."
                value={userSearchText}
                onChange={(e) => {
                  setUserSearchText(e.target.value)
                  setShowUserDropdown(true)
                }}
                onFocus={() => setShowUserDropdown(true)}
                className="w-48 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cb-accent"
              />
              {showUserDropdown && filteredUsers.length > 0 && (
                <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUserId(user.id)
                        setUserSearchText(user.label)
                        setShowUserDropdown(false)
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-cb-light transition-colors"
                    >
                      {user.label}
                    </button>
                  ))}
                </div>
              )}
              {/* "No users found" is an answer about the firm's user list, and
                  `filteredUsers` is `[]` both when the search matched nothing and
                  when the list was never read — `filteredUsers` opens with
                  `if (!allUsers) return []`. An admin approving on someone's
                  behalf would conclude that person has no account. So the three
                  unresolved states each get their own line, and only a completed
                  read is allowed to say "none". */}
              {showUserDropdown && (allUsersState === 'loading' || allUsersState === 'idle') && (
                <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
                  Loading users...
                </div>
              )}
              {showUserDropdown && (allUsersState === 'blocked' || allUsersState === 'failed') && (
                <div
                  role="alert"
                  className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-amber-200 rounded-lg shadow-lg px-3 py-2 text-xs text-amber-900"
                >
                  <p className="font-medium">
                    {allUsersState === 'blocked'
                      ? 'Can’t reach the server, so the user list wasn’t loaded.'
                      : 'We couldn’t load the user list.'}
                  </p>
                  <p className="mt-1 text-amber-800">This is not an empty firm — nobody was looked up.</p>
                  <button
                    type="button"
                    onClick={() => allUsersQuery.refetch()}
                    className="mt-2 rounded-lg border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              )}
              {showUserDropdown && allUsersState === 'ready' && userSearchText && filteredUsers.length === 0 && (
                <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
                  No users found
                </div>
              )}
            </div>
            {selectedUserId && (
              <button
                onClick={() => { setSelectedUserId(null); setUserSearchText('') }}
                className="text-xs text-cb-accent-text hover:underline"
              >
                Reset
              </button>
            )}
          </div>
        )}
      </div>

      {/* Version legend — inline, matching CenterbaseWeb style */}
      {snapshotData?.history?.userNames?.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-2 mb-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-black inline-block" />
            Original Prebill
            {snapshotData.history.versionDates?.[0] && (
              <span className="text-gray-500"> - {snapshotData.history.versionDates[0]}</span>
            )}
          </span>
          {snapshotData.history.userNames.map((name, i) => {
            if (name.toLowerCase() === 'original prebill') return null
            return (
              <span key={i} className="flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-sm inline-block"
                  style={{ backgroundColor: snapshotData.history.colors?.[i] || '#6b7280' }}
                />
                <span style={{ color: snapshotData.history.colors?.[i] || '#6b7280' }}>
                  {name}
                </span>
                {snapshotData.history.versionDates?.[i + 1] && (
                  <span className="text-gray-500"> - {snapshotData.history.versionDates[i + 1]}</span>
                )}
              </span>
            )
          })}
          <span className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-sm inline-block"
              style={{ backgroundColor: snapshotData.userColor || '#22c55e' }}
            />
            <span style={{ color: snapshotData.userColor || '#22c55e' }}>You</span>
          </span>
        </div>
      )}

      {/* Outside Approvals Banner */}
      <OutsideApprovalsBanner
        approvalData={approvalData}
        snapshotData={snapshotData}
        snapshotResolved={snapshotResolved}
      />

      {/* Toolbar */}
      <Toolbar
        bill={bill}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        showMarkup={showMarkup}
        setShowMarkup={setShowMarkup}
        showActions={showActions}
        setShowActions={setShowActions}
        onPrev={() => prevId && navigate(`/pre-bills/${prevId}`)}
        onNext={() => nextId && navigate(`/pre-bills/${nextId}`)}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onPrint={() => window.print()}
        editState={editState}
        onSaveAll={handleSaveAll}
        onDiscardAll={handleDiscardAll}
        saving={updateMutation.isPending}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => {
            /*
             * A tab badge is a count, and a count may only be drawn from a read
             * that completed. Both of these come from the line-items read, so
             * both stay off until it has. Today an unresolved read happens to
             * render no badge anyway — `count` lands on 0 and the `count > 0`
             * test below hides it — but that is luck, not a decision: the badge
             * is one edit away from reporting "(0)" on a bill nobody has read.
             *
             * The Held Entries badge in particular must not fall back to the
             * locally-held ids alone. That would be a partial count wearing the
             * clothes of a total, which is worse than no badge.
             */
            let count = 0
            if (tab === 'Line Items') count = lineItemsResolved ? allLineItems.filter((e) => !editState.removedEntryIds.has(e.id) && !editState.heldEntryIds.has(e.id)).length : 0
            if (tab === 'Held Entries') count = lineItemsResolved ? heldFromApi.length + editState.heldEntryIds.size : 0
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'text-cb-accent-text border-cb-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
                {count > 0 && (
                  <span className={`ml-1.5 text-xs ${
                    tab === 'Held Entries' ? 'text-amber-500' : 'text-gray-500'
                  }`}>
                    ({count})
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'Pre-Bill' && (
        previewLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : previewUnresolved ? (
          /* The invoice preview IS the pre-bill for most reviewers — it is the
             document they sign off. A blank or eternally-spinning tab reads as
             "this bill has nothing on it", so say what actually happened. */
          <div className="max-w-4xl mx-auto">
            <UnresolvedReadNotice
              state={previewState}
              subject="the invoice preview"
              notEmpty="This is not an empty invoice — the rendered pre-bill was never read, so nothing here shows what the client would receive."
              error={previewQuery.error}
              onRetry={() => previewQuery.refetch()}
            />
          </div>
        ) : billPreviewHtml ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 max-w-4xl mx-auto print:shadow-none print:border-0">
            <iframe
              srcDoc={billPreviewHtml}
              sandbox="allow-same-origin"
              title="Invoice Preview"
              className="w-full border-0 rounded-xl"
              style={{ minHeight: '600px' }}
              onLoad={(e) => {
                const doc = e.target.contentDocument
                if (doc?.body) {
                  e.target.style.height = doc.body.scrollHeight + 40 + 'px'
                }
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500">
            Invoice preview unavailable
          </div>
        )
      )}

      {activeTab === 'Line Items' && (
        <LineItemsTab
          timeEntries={timeEntries}
          expenses={expenses}
          isEditing={isEditing}
          showMarkup={showMarkup}
          editState={editState}
          dispatch={dispatch}
          isPosted={isPosted}
          onSaveEntry={handleSaveEntry}
          saving={updateMutation.isPending}
          lineItemsState={lineItemsState}
          lineItemsResolved={lineItemsResolved}
          lineItemsError={lineItemsQuery.error}
          onRetryLineItems={() => lineItemsQuery.refetch()}
        />
      )}

      {activeTab === 'Held Entries' && (
        <HeldEntriesTab
          allLineItems={allLineItems}
          editState={editState}
          dispatch={dispatch}
          heldFromApi={heldFromApi}
          lineItemsState={lineItemsState}
          lineItemsResolved={lineItemsResolved}
          lineItemsError={lineItemsQuery.error}
          onRetryLineItems={() => lineItemsQuery.refetch()}
        />
      )}

      {/* 'Unbilled Entries' is gone — see the note on TABS (CC-53439). */}
      {activeTab === 'Version History' && (
        <VersionHistoryTab
          snapshotData={snapshotData}
          snapshotState={snapshotState}
          snapshotError={snapshotQuery.error}
          onRetrySnapshot={() => snapshotQuery.refetch()}
        />
      )}
    </div>
  )
}

export default PreBillDetail
